import React, { useState, useEffect, useRef, memo } from 'react';
import styled from 'styled-components';
import {
  NotificationsOutlined,
  VideocamOutlined,
  ComputerOutlined,
  AccountCircleOutlined,
  LogoutOutlined,
  KeyboardArrowDownOutlined,
} from '@mui/icons-material';
import { AiOutlineCalculator } from 'react-icons/ai';
import { Typography, Box } from '@mui/material';
import zIndex from '@mui/material/styles/zIndex';
import useStudent from 'store/student/useStudent';
import useSurvey from 'store/survey/useSurvey';
import usePatient from 'store/patient/usePatient';
import useUser from 'store/user/useUser';
import { useNavigate } from 'react-router-dom';

import { imgSmartNurseLogoText } from 'assets';

const DropdownButton = styled.button`
  padding: 8px;
  margin-top: 26px;
  margin-right: 19px;
  width: auto;
  background-color: white;
  color: black;
  border: none;
  cursor: pointer;
  font-family: 'Spoqa', sans-serif;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  direction: row;
`;

const FontItem = styled.div`
  padding: 10px 0 2px 10px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  direction: row;
  &:hover {
    background-color: #ddd;
  }
`;

interface OwnProps {
  $visible: boolean;
}

const DropdownContent = styled.div<OwnProps>`
  display: ${props => (props.$visible ? 'block' : 'none')};
  position: absolute;
  background-color: #f9f9f9;
  min-width: 230px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  /* 아래로 펼쳐지므로 검색 툴바 행(zIndex.modal)·학생 미선택 안내 패널(modal + 1)보다 위여야 한다 */
  z-index: ${zIndex.modal + 2};
  top: 76px;
  border-radius: 8px;
  margin-right: 19px;
  /* left: 96px; */
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: flex;
  direction: row;
  justify-content: right;
`;

const fonts = [
  {
    icon: <VideocamOutlined />,
    label: '계정설정 및 수정',
  },
  {
    icon: <NotificationsOutlined />,
    label: '로그아웃',
  },
];

interface DropdownMenuProps {
  target: string;
}

function DropdownMenu({ target }: DropdownMenuProps) {
  const { onSignOut, college_ci, student_grade, student_name } = useUser();
  const { onResetStudent, student_name: userName } = useStudent();
  const { onResetPatient, patientInfo } = usePatient();
  const { onCloseReadOnly } = useSurvey();
  const navigate = useNavigate();

  // console.log('student_name', student_name);

  const [$visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleButtonClick = () => {
    setVisible(!$visible);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [ref, setVisible]);

  const handleFontSelect = (font: string) => {
    setVisible(false);
    if (font === '로그아웃') {
      onSignOut();
      // 로그아웃 시 스토어 초기화
      onResetStudent();
      onResetPatient();
      onCloseReadOnly();
    } else if (font === 'Screen Settings') {
      navigate('/screensetting');
      return;
    } else if (font === '계정설정 및 수정') {
      navigate('/mypage');
      return;
    }
  };

  const title = student_grade === 1 ? 'Nurse' : 'Prof./TA';

  return (
    <DropdownWrapper ref={ref}>
      <DropdownButton type="button" onClick={() => handleButtonClick()}>
        <Box
          height={25}
          component="img"
          alt={'SmartNurse'}
          src={college_ci ? college_ci : imgSmartNurseLogoText}
          sx={{ objectFit: 'contain', marginTop: '5px' }}
        />
        <div
          style={{
            marginLeft: '30px',
            width: 'auto',
            whiteSpace: 'nowrap',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'right',
            cursor: 'pointer',
          }}
        >
          <Typography sx={{ fontSize: '8px', textAlign: 'right' }}>
            {title}
          </Typography>
          {student_name}
        </div>
        <KeyboardArrowDownOutlined
          sx={{ marginLeft: '8px', marginTop: '5px', cursor: 'pointer' }}
        />
      </DropdownButton>
      <DropdownContent $visible={$visible}>
        <div
          style={{
            flexDirection: 'column',
            justifyContent: 'right',
          }}
        >
          {fonts.map(v => {
            return (
              <FontItem key={v.label} onClick={() => handleFontSelect(v.label)}>
                <div style={{ paddingTop: '4px' }}>
                  {v.label === 'Screen Settings' ? (
                    <ComputerOutlined sx={{ fontSize: '15px' }} />
                  ) : v.label === '계정설정 및 수정' ? (
                    <AccountCircleOutlined sx={{ fontSize: '15px' }} />
                  ) : (
                    <LogoutOutlined sx={{ fontSize: '15px' }} />
                  )}
                </div>
                <div style={{ marginLeft: '10px' }}>{v.label}</div>
              </FontItem>
            );
          })}
        </div>
      </DropdownContent>
    </DropdownWrapper>
  );
}

export default memo(DropdownMenu);
